// Lot 749 — catalogue des sections du menu admin réglables rôle par rôle (Paramètres › Privilèges › « Sections
// visibles par rôle »). Miroir des entrées non « super admin seulement » de `pages/Admin.jsx` (rawGroups) : une section
// n'est proposée à un rôle que si ses permissions (matrice des rôles) la lui ouvrent ; la case décide ensuite de
// l'afficher ou non dans son menu. `rolesOnly` : entrées du groupe Finances qui diffèrent selon le rôle.
export const CONFIGURABLE_ROLES = ["manager", "moderator", "associate", "support"];
export const ROLE_LABELS = { manager: "Manager", moderator: "Modérateur", associate: "Associé", support: "Support" };
export const DEFAULT_HIDDEN_SECTIONS = { associate: ["monetization"] };

export const ADMIN_SECTIONS = [
  { key: "dashboard", label: "Vue d'ensemble", group: "Général", perm: "dashboard", rolesOnly: ["manager", "moderator", "associate"] },
  { key: "users", label: "Utilisateurs", group: "Utilisateurs", perm: "users" },
  { key: "kyc", label: "Vérifications KYC", group: "Utilisateurs", perm: "kyc" },
  { key: "installs", label: "Installations par pays", group: "Utilisateurs", perm: "users" },
  { key: "summary", label: "Résumés (stats membres)", group: "Utilisateurs", perm: "users" }, // lot 766
  { key: "active", label: "Connectés / inactifs", group: "Utilisateurs", perm: "users" }, // lot 765
  { key: "churches", label: "Églises", group: "Communauté", perm: "churches" },
  { key: "resources", label: "Ressources (articles)", group: "Contenu", perm: "resources", needsManage: true },
  { key: "triage", label: "À traiter", group: "Modération & Support", perm: "moderation" },
  { key: "moderation", label: "Modération", group: "Modération & Support", perm: "moderation" },
  { key: "tickets", label: "Tickets support", group: "Modération & Support", perm: "tickets" },
  { key: "monetization", label: "Parrainage & points", group: "Monétisation", perm: "finance" },
  { key: "ads", label: "Publicités", group: "Monétisation", perm: "finance", needsManage: true },
  { key: "promos", label: "Promos Marketplace", group: "Monétisation", perm: "moderation" },
  { key: "accounting", label: "Comptabilité", group: "Finances", perm: "finance", rolesOnly: ["manager", "associate"] },
  { key: "associates", label: "Associés (ma carte)", group: "Finances", perm: "finance", rolesOnly: ["associate"] }, // lot 758
  { key: "donations", label: "Dons · Vérifié · Crédits pub", group: "Finances", perm: "finance", rolesOnly: ["moderator", "support"] },
  { key: "transactions", label: "Comptabilité", group: "Finances", perm: "finance", rolesOnly: ["moderator", "support"] },
  { key: "notifications", label: "Notifications", group: "Communication", perm: "notifications" },
  { key: "ai", label: "Assistant IA", group: "Configuration", perm: "ai" },
];

// Une section est « accessible » à un rôle si la matrice lui donne la capacité (et « gérer » quand requis).
export const sectionAllowed = (section, role, levels) => {
  if (section.rolesOnly && !section.rolesOnly.includes(role)) return false;
  const lvl = (levels || {})[section.perm];
  if (!lvl) return false;
  return !section.needsManage || lvl === "manage";
};

export const isSectionHidden = (hidden, role, key) => ((hidden && hidden[role]) || DEFAULT_HIDDEN_SECTIONS[role] || []).includes(key);

// Lot 758 — « coche et donne la permission d'un coup ». Une case est « structurelle » (jamais cochable) quand la
// section n'existe pas pour ce rôle (rolesOnly) ou pour Support + vue d'ensemble (console tickets seuls).
export const sectionStructural = (section, role) => (!!section.rolesOnly && !section.rolesOnly.includes(role)) || (role === "support" && section.perm === "dashboard");
export const requiredLevel = (section) => (section.needsManage ? "manage" : "view");
const LEVEL_RANK = { none: 0, view: 1, manage: 2 };
export const levelsWithGrants = (levels, grants) => {
  const out = { ...(levels || {}) };
  for (const [cap, lvl] of Object.entries(grants || {})) if (LEVEL_RANK[lvl] > LEVEL_RANK[out[cap] || "none"]) out[cap] = lvl;
  return out;
};
// Cases cochées par rôle d'après les sections enregistrées (ouvertes par la matrice et non masquées).
export const checkedFromHidden = (hidden, rolesLevels) => Object.fromEntries(CONFIGURABLE_ROLES.map((r) => [
  r, ADMIN_SECTIONS.filter((s) => sectionAllowed(s, r, (rolesLevels || {})[r]) && !isSectionHidden(hidden, r, s.key)).map((s) => s.key),
]));
// À partir des cases cochées : permissions à accorder (jamais retirées) + sections masquées (ouvertes mais décochées).
// `oneClick` = false → une case cochée sans permission est simplement ignorée (comportement lot 749).
export const planRoleSections = (checked, rolesLevels, oneClick = true) => {
  const hidden = {}, grants = {};
  for (const role of CONFIGURABLE_ROLES) {
    const sel = new Set(checked[role] || []);
    const levels = (rolesLevels || {})[role] || {};
    const g = {};
    if (oneClick) {
      for (const s of ADMIN_SECTIONS) {
        if (sectionStructural(s, role) || !sel.has(s.key) || sectionAllowed(s, role, levels)) continue;
        const need = requiredLevel(s);
        if (LEVEL_RANK[need] > LEVEL_RANK[g[s.perm] || "none"]) g[s.perm] = need;
      }
    }
    const after = levelsWithGrants(levels, g);
    hidden[role] = ADMIN_SECTIONS.filter((s) => !sectionStructural(s, role) && sectionAllowed(s, role, after) && !sel.has(s.key)).map((s) => s.key);
    if (Object.keys(g).length) grants[role] = g;
  }
  return { hidden, grants };
};

// Lot 750 — « Voir en tant que » depuis la carte : les cases EN COURS (non enregistrées) voyagent par sessionStorage
// jusqu'à la console (`Admin.jsx` les applique à la place des sections enregistrées tant que l'aperçu est ouvert).
export const ROLE_SECTIONS_DRAFT_KEY = "yalola.admin.roleSectionsDraft";
export const readRoleSectionsDraft = () => {
  try { const d = JSON.parse(sessionStorage.getItem(ROLE_SECTIONS_DRAFT_KEY) || "null"); return d && d.hidden ? d : null; } catch { return null; }
};
export const writeRoleSectionsDraft = (role, hidden, grants = {}, checked = null) => { try { sessionStorage.setItem(ROLE_SECTIONS_DRAFT_KEY, JSON.stringify({ role, hidden, grants, ...(checked ? { checked } : {}) })); } catch { /* noop */ } };
export const clearRoleSectionsDraft = () => { try { sessionStorage.removeItem(ROLE_SECTIONS_DRAFT_KEY); } catch { /* noop */ } };
export const ROLE_SECTIONS_SAVED_EVENT = "yalola:role-sections";
